"use client";
import NotificationPopup from "@/components/participant-portal/notification-popup";
import { useEvent } from "@/context/event-context";
import { useWhiteLabel } from "@/context/white-label-context";
import { useHeaderAccentColor } from "@/hooks/use-header-accent-color";
import { useTranslation } from "@/lib/sajilni-localization";
import { useSession } from "@sajilni/auth/react";
import { changeLanguage, useSetLocale } from "@sajilni/localization";
import { getInitials } from "@sajilni/utils/getInitials";
import { cn } from "@sajilni/utils/cn";
import { BrandingEntityType } from "database";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState } from "react";
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui";
import { toStorageUrl, isStorageUrl } from "@sajilni/utils/image-url";
import { UserProfileMenu } from "./UserProfileMenu";
import { useBrandingStore, selectEffectiveLogo } from "@/zustand/brandingStore";
import { DEFAULT_BRANDING_LOGO } from "@/lib/branding/constants";

function isPlatformDefaultLogo(url?: string | null) {
  return (
    !url ||
    url === "/sajilni-logo.png" ||
    url === DEFAULT_BRANDING_LOGO
  );
}

export const Logo = ({
  endComponent,
  userName,
  width = 122,
  height = 32,
  src = "/sajilni-logo.png",
  hideUserProfile = false,
  type,
  notificationPopup,
  hasNetworkingHub = false,
  isParticipantsPortalPage = false,
  level,
  isEventWallPage,
}: {
  endComponent?: React.ReactNode;
  userName?: string;
  width?: number;
  height?: number;
  src?: string;
  hideUserProfile?: boolean;
  type?: BrandingEntityType;
  notificationPopup?: boolean;
  hasNetworkingHub?: boolean;
  isParticipantsPortalPage?: boolean;
  level?: "ADMIN" | "TEAM" | "SETUP" | null;
  isEventWallPage?: boolean;
}) => {
  const { data: session } = useSession();
  const { t, i18n } = useTranslation("sajilni2.0");
  const currentLanguageCode = i18n.resolvedLanguage;
  const setLocale = useSetLocale();
  const router = useRouter();
  const [profileDropDownOpen, setProfileDropDownOpen] = useState(false);
  const pathname = usePathname();
  const isLoginPage =
    [
      "/login",
      "/forget-password",
      "/set-password",
      "/verify-request",
      "/verify-email",
      "/signup",
    ].includes(pathname ?? "") || (pathname?.startsWith("/signup/") ?? false);
  const onEventRoute =
    (pathname?.includes("/event/") && !pathname?.includes("/admin/")) ?? false;

  // Get accent color for header styling (from branding store)
  const { accentColor } = useHeaderAccentColor();
  const storeLogo = useBrandingStore(selectEffectiveLogo);

  // Get event and white label data safely
  let languages: any[] = [];
  let organizationName: string | undefined;
  let whiteLabelName: string | undefined;
  let wlBrandingLogo: string | undefined;
  let eventAccentFromContext: string | null = null;
  let hasWhiteLabelTenant = false;

  try {
    const eventData = useEvent();
    languages = eventData.languages || [];
    organizationName = (eventData.event as any)?.organizations?.[0]
      ?.translations?.[0]?.name;
  } catch {
    // Not in EventProvider context
  }

  try {
    const whiteLabelData = useWhiteLabel();
    whiteLabelName = whiteLabelData.whiteLabel?.name || undefined;
    wlBrandingLogo = whiteLabelData.branding?.logo || undefined;
    eventAccentFromContext = whiteLabelData.eventAccentColor;
    hasWhiteLabelTenant = Boolean(whiteLabelData.whiteLabel?.id);
  } catch {
    // Not in WhiteLabelProvider context
  }

  // Event accent from store/context; CSS --event-header-accent is SSR fallback on event routes
  const headerColor = accentColor || eventAccentFromContext || null;
  const headerBgStyle = headerColor
    ? { backgroundColor: headerColor }
    : onEventRoute
      ? { backgroundColor: "var(--event-header-accent, #667085)" }
      : undefined;
  const headerStroke =
    headerColor ||
    (onEventRoute
      ? "var(--event-header-accent, #667085)"
      : "var(--primary-500)");

  // Get organization or white label name for initials
  const displayName = organizationName || whiteLabelName;
  const initials = getInitials(displayName);

  // Off event routes on a WL tenant, prefer WL logo and never show platform Sajilni.
  // On platform /admin (no WL tenant), keep the Sajilni default logo.
  const candidates = onEventRoute
    ? [storeLogo, src, wlBrandingLogo]
    : [wlBrandingLogo, src, storeLogo];
  const preferred = candidates.find((c) => !isPlatformDefaultLogo(c));
  const rawSrc =
    preferred ??
    (hasWhiteLabelTenant
      ? ""
      : src || storeLogo || DEFAULT_BRANDING_LOGO || "/sajilni-logo.png");
  const finalSrc = toStorageUrl(rawSrc) || undefined;
  const isStorageLogo = Boolean(finalSrc && isStorageUrl(finalSrc));
  const hasLogo = Boolean(finalSrc?.trim());

  return (
    <div className="flex h-[108px] w-full items-center justify-between gap-2 border-b-[1px] border-b-[#EAECF0] px-3 sm:px-6 lg:px-16">
      <div className="flex shrink-0 items-center">
        <Link href="/">
          {hasLogo ? (
            <Image
              className="h-auto w-auto max-w-[122px]"
              src={finalSrc as string}
              alt="logo"
              width={width}
              height={height}
              loading="eager"
              priority
              unoptimized={isStorageLogo}
            />
          ) : (
            <Avatar className="h-10 w-10">
              <AvatarFallback
                className={cn(
                  "logo-header-accent-bg text-base font-medium uppercase text-white",
                  !headerColor && !onEventRoute && "!bg-primary"
                )}
                style={headerBgStyle}
              >
                {initials}
              </AvatarFallback>
            </Avatar>
          )}
        </Link>
      </div>
      {/* This was commented on as part of a discussion with Omar. */}
      {/* <div className='relative w-[76%]'>
            <Input className='!pl-[37px] h-[44px] outline-none text-[#667085] border-[1px] border-[solid] border-[#D0D5DD] font-normal' placeholder='Search...' />
            <svg xmlns="http://www.w3.org/2000/svg" className={"absolute top-[11px] left-[10px]"} width={20} height={20} viewBox="0 0 18 18" fill="none">
              <path d="M16.5 16.5L13.5834 13.5833M15.6667 8.58333C15.6667 12.4954 12.4954 15.6667 8.58333 15.6667C4.67132 15.6667 1.5 12.4954 1.5 8.58333C1.5 4.67132 4.67132 1.5 8.58333 1.5C12.4954 1.5 15.6667 4.67132 15.6667 8.58333Z"
                stroke={"#667085"}strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                </div> */}
      <div className="flex min-w-0 items-center gap-2 sm:gap-4">
        {endComponent}
        {
          <DropdownMenu
            open={profileDropDownOpen}
            onOpenChange={setProfileDropDownOpen}
          >
            <DropdownMenuTrigger
              className="flex items-center justify-center"
              aria-label="Select language"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="logo-header-accent-stroke"
                style={{ color: headerStroke }}
              >
                <path
                  d="M8.00065 1.33301C9.66817 3.15858 10.6158 5.5277 10.6673 7.99967C10.6158 10.4717 9.66817 12.8408 8.00065 14.6663M8.00065 1.33301C6.33313 3.15858 5.38548 5.5277 5.33398 7.99967C5.38548 10.4717 6.33313 12.8408 8.00065 14.6663M8.00065 1.33301C4.31875 1.33301 1.33398 4.31778 1.33398 7.99967C1.33398 11.6816 4.31875 14.6663 8.00065 14.6663M8.00065 1.33301C11.6826 1.33301 14.6673 4.31778 14.6673 7.99967C14.6673 11.6816 11.6826 14.6663 8.00065 14.6663M1.66733 5.99967H14.334M1.66732 9.99967H14.334"
                  stroke="currentColor"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="min-w-[160px]">
              {(
                (languages && languages.length
                  ? languages
                  : [
                      { code: "en", name: "English" },
                      { code: "ar", name: "العربية" },
                    ]) as { code: string; name: string }[]
              ).map((lng) => (
                <DropdownMenuItem
                  className="ui-py-3 hover:ui-bg-gray-100 hover:ui-text-gray-700"
                  key={lng.code}
                  onClick={async () => {
                    if (currentLanguageCode !== lng.code) {
                      setLocale(lng.code as any);
                      await changeLanguage(lng.code as any);
                    }
                  }}
                >
                  {lng.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        }
        {session ? (
          <>
            {notificationPopup && <NotificationPopup />}
            <UserProfileMenu
              isEventWallPage={isEventWallPage}
              level={level}
              userName={userName}
              showAllMenuItems={notificationPopup}
              isParticipantsPortalPage={isParticipantsPortalPage}
            />
          </>
        ) : (
          !isLoginPage && (
            <DropdownMenu>
              <DropdownMenuTrigger
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium text-white transition-colors",
                  !headerColor && "bg-primary hover:bg-primary/90"
                )}
                style={headerBgStyle}
              >
                {t("Sign In")}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[180px]">
                <DropdownMenuItem
                  className="ui-py-3 hover:ui-bg-gray-100 hover:ui-text-gray-700"
                  asChild
                >
                  <Link href="/login?type=admin">
                    {t("Sign In as Organizer")}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="ui-py-3 hover:ui-bg-gray-100 hover:ui-text-gray-700"
                  asChild
                >
                  <Link href="/login?type=attendee">
                    {t("Sign In as Attendee")}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        )}
      </div>
    </div>
  );
};
